// What the God-only "Post to USGA" rows show for a score: its posting status from usgaPost, a
// pending request (a batch the server hasn't finished), or — not posted yet — whether it's ready.
import { USGA_POST_STATUSES } from '../types/UsgaPost';

import type { UsgaPost } from '../types/UsgaPost';
import type { UsgaPostBuild } from './usgaScorePostUtils';

export type UsgaPostStatusColor = 'default' | 'info' | 'success' | 'warning' | 'error';

export interface UsgaPostRowStatus {
  label: string;
  color: UsgaPostStatusColor;
  detail: string | null;
}

/** A score left POSTING longer than this was abandoned (matches the server's STALE_POSTING_MS). */
export const STALE_POSTING_MS = 10 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const millis = (t: unknown): number | null =>
  t && typeof (t as { toMillis?: unknown }).toMillis === 'function' ? (t as { toMillis(): number }).toMillis() : null;

const shortDate = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export function usgaPostRowStatus(args: {
  post: UsgaPost | null | undefined;
  isRequested: boolean;
  build: UsgaPostBuild;
  now: number;
  accessWaitDays: number;
}): UsgaPostRowStatus {
  const { post, isRequested, build, now, accessWaitDays } = args;
  if (isRequested) return { label: 'Requested', color: 'info', detail: null };

  switch (post?.status) {
    case USGA_POST_STATUSES.POSTING: {
      const started = millis(post.postingStartedAt);
      return started !== null && now - started >= STALE_POSTING_MS
        ? { label: 'Interrupted', color: 'warning', detail: 'Posting stopped before it finished. Posting again first checks the player\'s USGA record.' }
        : { label: 'Posting', color: 'info', detail: null };
    }
    case USGA_POST_STATUSES.POSTED: {
      const differential = post.differential != null ? ` · differential ${post.differential}` : '';
      return { label: `Posted #${post.usgaScoreId}`, color: 'success', detail: `Adjusted score ${post.adjustedGrossScore ?? '—'}${differential}` };
    }
    case USGA_POST_STATUSES.UNDER_REVIEW:
      return { label: 'Under review', color: 'warning', detail: post.validationMessage ?? 'The USGA has a matching score for this golfer that day. Confirm to keep this one.' };
    case USGA_POST_STATUSES.WAITING_FOR_ACCESS: {
      const since = millis(post.waitingSince);
      const dates = since === null ? '' : ` Since ${shortDate(since)}; gives up ${shortDate(since + accessWaitDays * DAY_MS)}.`;
      return { label: 'Waiting for approval', color: 'warning', detail: `${post.lastError?.message ?? 'Waiting for the player to approve Gratigolf with the USGA.'}${dates}` };
    }
    case USGA_POST_STATUSES.ACCESS_NOT_GRANTED:
      return { label: 'Approval not given', color: 'error', detail: post.lastError?.message ?? null };
    case USGA_POST_STATUSES.UNKNOWN:
      return { label: 'Unknown', color: 'warning', detail: post.lastError?.message ?? null };
    case USGA_POST_STATUSES.ERROR:
      return { label: 'Error', color: 'error', detail: post.lastError?.message ?? null };
  }

  return build.ok
    ? { label: 'Ready', color: 'default', detail: null }
    : { label: 'Can\'t post', color: 'default', detail: build.block.message };
}

/**
 * What a row lets God do: post it; post it again after a failed or unfinished attempt, or one
 * waiting on (or given up on) the golfer's approval — e.g. once a wrong Handicap ID or email is
 * fixed; or confirm it.
 */
export type UsgaPostRowAction = 'post' | 'postAgain' | 'confirm' | null;

/**
 * The action for a row. Posting needs a score Gratigolf can post — or one blocked only by the
 * posting season, which the staging-only "send anyway" can override to test the USGA's response.
 */
export function usgaPostRowAction(args: {
  post: UsgaPost | null | undefined;
  isRequested: boolean;
  build: UsgaPostBuild;
  now: number;
}): UsgaPostRowAction {
  const { post, isRequested, build, now } = args;
  if (isRequested) return null;
  const postable = build.ok || build.block.code === 'OUT_OF_SEASON';

  switch (post?.status) {
    case undefined:
      return postable ? 'post' : null;
    case USGA_POST_STATUSES.UNDER_REVIEW:
      return 'confirm';
    case USGA_POST_STATUSES.ERROR:
    case USGA_POST_STATUSES.UNKNOWN:
    case USGA_POST_STATUSES.WAITING_FOR_ACCESS:
    case USGA_POST_STATUSES.ACCESS_NOT_GRANTED:
      return postable ? 'postAgain' : null;
    case USGA_POST_STATUSES.POSTING: {
      const started = millis(post.postingStartedAt);
      return postable && started !== null && now - started >= STALE_POSTING_MS ? 'postAgain' : null;
    }
    default:
      return null;
  }
}
